<script setup lang="ts">
// 여러 줄 로딩 스켈레톤. "불러오는 중…" 문구 대신 실제 내용과 비슷한 모양·높이로 자리를 채운다.
//  - text : 문단 (줄마다 너비를 달리하고 마지막 줄은 짧게)
//  - meta : 라벨 + 값 상자 (데이터 상세 .meta-item 과 같은 높이)
//  - table: 표 행 (데이터 상세 .meta-table 과 같은 높이, 칸 columns 개)
const props = withDefaults(defineProps<{
  lines?: number
  variant?: 'text' | 'meta' | 'table'
  columns?: number
}>(), {
  lines: 3,
  variant: 'text',
  columns: 2,
})

const TEXT_WIDTHS = ['100%', '92%', '96%', '88%']

function textWidth(index: number): string {
  if (props.lines > 1 && index === props.lines - 1) return '60%'
  return TEXT_WIDTHS[index % TEXT_WIDTHS.length]!
}
</script>

<template>
  <div class="skeleton-lines" :class="`variant-${variant}`" role="status">
    <span class="sr-only">불러오는 중</span>

    <template v-if="variant === 'meta'">
      <div v-for="i in lines" :key="i" class="meta-row" aria-hidden="true">
        <div class="meta-label-line"><span class="skeleton bar" style="width: 35%; height: 9px;"></span></div>
        <div class="meta-value-line"><span class="skeleton bar" :style="{ width: i % 2 ? '55%' : '45%', height: '11px' }"></span></div>
      </div>
    </template>

    <div v-else-if="variant === 'table'" class="table-box" aria-hidden="true">
      <div v-for="i in lines" :key="i" class="table-row">
        <div v-for="c in columns" :key="c" class="table-cell" :class="{ first: c === 1 }">
          <span class="skeleton bar" :style="{ width: c === 1 ? '60%' : (i % 2 ? '70%' : '50%'), height: '12px' }"></span>
        </div>
      </div>
    </div>

    <template v-else>
      <div v-for="(_, index) in lines" :key="index" class="text-line" aria-hidden="true">
        <span class="skeleton bar" :style="{ width: textWidth(index), height: '13px' }"></span>
      </div>
    </template>
  </div>
</template>

<style scoped>
.bar {
  display: block;
  border-radius: 4px;
}

/* text: 13.5px 글자 / line-height 1.75 에 맞춘 줄 높이 */
.variant-text {
  display: flex;
  flex-direction: column;
}
.text-line {
  height: 23px;
  display: flex;
  align-items: center;
}

/* meta: .meta-item (padding 6px 10px, 라벨 10px + 값 12px) */
.variant-meta {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.meta-row {
  padding: 6px 10px;
  border-radius: 5px;
  background-color: #ffffff;
  border: 1px solid #F3F4F6;
}
.meta-label-line {
  height: 15px;
  margin-bottom: 2px;
  display: flex;
  align-items: center;
}
.meta-value-line {
  height: 18px;
  display: flex;
  align-items: center;
}

/* table: .meta-table 행 (padding 9px 14px, 13px 글자) */
.table-box {
  border: 1px solid #E5E7EB;
  border-radius: 8px;
  overflow: hidden;
}
.table-row {
  display: flex;
  border-bottom: 1px solid #F3F4F6;
}
.table-row:last-child {
  border-bottom: none;
}
.table-row:nth-child(even) {
  background-color: #FAFAFA;
}
.table-cell {
  flex: 1;
  height: 38px;
  padding: 0 14px;
  display: flex;
  align-items: center;
}
.table-cell.first {
  flex: 0 0 200px;
  max-width: 40%;
  border-right: 1px solid #F3F4F6;
}
</style>
